// src/ui/Tooltip.tsx (T1): short supplemental text. The owner rejects paragraphs in cards, so caveats live in an
// InfoTip: a small (i) button that shows one or two sentences on hover, focus or tap. Tooltip adds a hover and
// focus label to a control that already has its own accessible name. Root classes: hb-tip, hb-tooltip.
import { cloneElement, type ComponentChildren, type VNode } from "preact";
import { useId, useRef, useState } from "preact/hooks";
import { cx } from "./cx";
import { Icon } from "./Icon";
import { useDismiss, usePopoverPlacement } from "./popover";

export function InfoTip(props: {
  /** The button's accessible name, e.g. "About this estimate". */
  label: string;
  children: ComponentChildren;
  align?: "start" | "center" | "end";
  /** On a navy panel. */
  onNavy?: boolean;
  class?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const id = useId();
  usePopoverPlacement(open, btn, pop, props.align ?? "center");
  useDismiss(open, [btn, pop], () => {
    setOpen(false);
    setPinned(false);
  });
  return (
    <span
      class={cx("hb-tip", props.onNavy && "hb-tip-navy", props.class)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        if (!pinned) setOpen(false);
      }}
    >
      <button
        ref={btn}
        type="button"
        class="hb-tip-btn"
        aria-label={props.label}
        aria-expanded={open ? "true" : "false"}
        aria-describedby={open ? id : undefined}
        onClick={() => {
          const next = !(open && pinned);
          setPinned(next);
          setOpen(next);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (!pinned) setOpen(false);
        }}
      >
        <Icon name="info" size="sm" />
      </button>
      <span ref={pop} id={id} role="tooltip" class="hb-tip-pop" hidden={!open}>
        {props.children}
      </span>
    </span>
  );
}

/** A hover and focus label for one child element (which keeps its own accessible name). */
export function Tooltip(props: { text: string; children: VNode; align?: "start" | "center" | "end" }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const id = useId();
  usePopoverPlacement(open, wrap, pop, props.align ?? "center");
  useDismiss(open, [wrap], () => setOpen(false));
  return (
    <span
      ref={wrap}
      class="hb-tooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusIn={() => setOpen(true)}
      onFocusOut={() => setOpen(false)}
    >
      {cloneElement(props.children, { "aria-describedby": id })}
      <span ref={pop} id={id} role="tooltip" class="hb-tip-pop hb-tip-pop-dark" hidden={!open}>
        {props.text}
      </span>
    </span>
  );
}
